import { effect, inject, Injector, Signal, untracked } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { AlertStore } from '@alert/data-access';
import { HttpErrorResponse } from "@angular/common/http";

export interface IErrorStore {
  error: any;
}

export const initialErrorStore: IErrorStore = {
  error: null,
};

export const ErrorStore = signalStore(
  {providedIn: 'root'},
  withState(initialErrorStore),
  withMethods((state, alertStore = inject(AlertStore)) => {

    const getHttpErrorObject = (error: HttpErrorResponse): any => {
      if (error?.error?.ValidationResult) {
        return error.error.ValidationResult;
      }
    
      if (error?.error?.Errors?.length) {
        return error.error.Errors;
      }
      if (error?.error?.errors) {
        return  error.error.errors;
      }
      return error
    }

    /**
     * Formatos de erro que os serviços da plataforma devolvem, na ordem em que
     * são testados. Cada extrator devolve `{ value }` quando reconhece o corpo
     * (mesmo que o valor seja vazio) e `null` para passar ao seguinte — a mesma
     * semântica da cadeia de `if`s que existia aqui antes.
     */
    const httpErrorExtractors: ((payload: any) => { value: any } | null)[] = [
      (payload) => (typeof payload?.message === 'string' && payload.message ? { value: payload.message } : null),
      (payload) => (typeof payload?.Message === 'string' && payload.Message ? { value: payload.Message } : null),
      // Contrato dos Workers Cloudflare (linkbio, suggestions…): `{ error: string }`.
      (payload) => (typeof payload?.error === 'string' && payload.error ? { value: payload.error } : null),
      (payload) => {
        if (!Array.isArray(payload?.Errors)) return null;
        const messages = payload.Errors.map((element: any) => element?.ErrorMessage).filter(Boolean);
        return messages.length ? { value: messages.join('\n') } : null;
      },
      (payload) => (Array.isArray(payload?.Erros) ? { value: payload.Erros[0] } : null),
      (payload) => {
        if (!payload?.errors) return null;
        const errorKeys = Object.keys(payload.errors);
        return { value: payload.errors[errorKeys[0]][0] };
      },
      (payload) => (payload?.Validacao?.Erros ? { value: payload.Validacao.Erros } : null),
      // Todas as mensagens, não só a primeira: uma recusa pode trazer um erro
      // por item (ex.: um "Horário … sem vagas" para cada horário lotado).
      (payload) =>
        payload?.ValidationResult?.Errors
          ? {
              value: payload.ValidationResult.Errors.map((element: any) =>
                typeof element === 'object' ? element?.ErrorMessage : element,
              )
                .filter(Boolean)
                .join('\n'),
            }
          : null,
      (payload) => (payload?.ModelState?.Erros ? { value: payload.ModelState.Erros } : null),
      (payload) => (payload?.errors?.DomainValidations ? { value: payload.errors.DomainValidations } : null),
      (payload) => (payload?.Erros ? { value: payload.Erros } : null),
      (payload) => {
        const messages = payload?.Validacao?.Errors?.map((element: any) => element?.Message).filter(Boolean);
        return messages?.length ? { value: messages.join('\n') } : null;
      },
    ];

    const getHttpErrorMessage = (error: HttpErrorResponse): string | null => {
      const payload = error?.error;

      for (const extract of httpErrorExtractors) {
        const hit = extract(payload);
        if (hit) return hit.value;
      }

      return null;
    }

    const handleHttpError = (error: HttpErrorResponse, title?: string, message?: string): void => {
      const errorMessage: string | null = getHttpErrorMessage(error) || message;

      if (errorMessage) openAlert(errorMessage, title);
      else {
        if (error.status === 400) openAlert('ALERT.ERROR_400', title);
        if (error.status === 404) openAlert('ALERT.ERROR_404', title);
        if (error.status === 401 || error.status === 403) openAlert('ALERT.ERROR_401', title);
        if (error.status === 500) openAlert('ALERT.GENERIC_ERROR_MESSAGE', title, true);
        // `status === 0` não diz que a ação falhou — diz que a resposta não
        // chegou, e o servidor pode muito bem tê-la executado. Por isso o
        // título do chamador é ignorado de propósito: tanto ele quanto o
        // genérico ("Não foi possível concluir a ação") afirmam um fracasso que
        // ninguém observou.
        if (error.status === 0) openAlert('ALERT.GENERIC_ERROR_MESSAGE', 'ALERT.UNCONFIRMED_TITLE');
      }
    }

    /**
     * `untracked` porque este store é escrito a partir de contextos que não
     * controlamos — ver a nota em `setJavascriptError`. O alerta escreve no
     * `AlertStore`, e uma escrita de signal herdada de um `computed`/template é
     * recusada com NG0600.
     */
    const openAlert = (message: string, title?: string, isDanger?: boolean) => {
      untracked(() =>
        alertStore.create({
          message: message ?? 'ALERT.GENERIC_ERROR_MESSAGE',
          title: title ?? 'ALERT.GENERIC_ERROR_TITLE',
          type: isDanger ? 'danger' : 'warning'
        })
      );
    }


    const handleError = ({ error, title, message }: { error?: HttpErrorResponse | Error, title?: string, message?: string } = { }) => {
      if (error instanceof HttpErrorResponse) {
        handleHttpError(error, title, message)
        return;
      }
      openAlert(error?.message || message, title);
    }
    return {
      getHttpErrorObject,
      getHttpErrorMessage,
      handleError,
      setHttpError: (payload: HttpErrorResponse) => {
        untracked(() => patchState(state, { error: payload }));
        handleHttpError(payload);
      },
      /**
       * Escrito pelo `ErrorHandler` global, e por isso **nunca** pode lançar.
       *
       * O Angular chama o handler a partir do `catch` do `tick()`, e há um caso
       * em que o contexto reativo do change detection ainda está ativo: o
       * NG0101 (`tick()` reentrante) é lançado em `tickImpl` **antes** do
       * `setActiveConsumer(null)`. Sem o `untracked`, este `patchState` herdava
       * esse consumer, rebentava com NG0600 e engolia o erro original.
       */
      setJavascriptError: (payload: any) => {
        untracked(() => patchState(state, { error: payload }));
      },
      createErrorAlertsBySignals: (configs: { signals: Signal<HttpErrorResponse | Error>[]; title?: string; message?: string }[], injector: Injector) => {
        for (const { signals, title, message } of configs) {
          for (const signal of signals) {
            effect(() => {
              const error = signal();
              if (error) untracked(() => handleError({ error, title, message }));
            }, { injector })
          }
        }
      }
    }
  })

);
